import flatten from "lodash/flatten";
import flow from "lodash/flow";
import isEqual from "lodash/isEqual";
import set from "lodash/set";
import transformKeys from "./TransformKeys";
const keySeparator = "\u2E31";
const humanSeparator = "-";
const _defaultKeyJoiner = (keys) => keys.join(keySeparator);
const _defaultChartGenerator = ({ type, name, labels, values, originalName }) => ({
  type,
  name,
  x: labels,
  y: values,
  originalName
});
export const flattenLeafs = (leafs, matcher = ({ source }) => source.endsWith("leaf")) => flatten(leafs.map((l) => l.values.filter((value) => matcher(value)).map((v) => [l.key, v])));
export const formatSeries = ({
  valuesBySeries = {},
  xLabels = [],
  seriesFullTracePathMapper = {}
}) => Object.keys(valuesBySeries).map((value) => [
  value,
  xLabels,
  valuesBySeries[value],
  [],
  seriesFullTracePathMapper[value]
]);
const isLeaf = (row) => row.source === "leaf";
export const getLeafsFromRows = (rows) => rows.filter(isLeaf);
export const getXLabelsFromLeafs = (leafs) => leafs.map(({ key }) => key);
export const extractSeries = (keyJoiner = _defaultKeyJoiner, leafValueMatcher = void 0) => (results) => {
  const leafs = getLeafsFromRows(results);
  const xLabels = getXLabelsFromLeafs(leafs);
  const flatLeafs = flattenLeafs(leafs, leafValueMatcher);
  const valuesBySeries = {};
  const seriesFullTracePathMapper = {};
  flatLeafs.forEach(([key, value]) => {
    const joinedKey = keyJoiner(value.key);
    const targetIdx = xLabels.findIndex((l) => isEqual(l, key));
    if (value.value !== null && value.value !== void 0) {
      set(valuesBySeries, [joinedKey, targetIdx], value.value);
      seriesFullTracePathMapper[joinedKey] = _defaultKeyJoiner(value.key);
    }
  });
  return { valuesBySeries, xLabels, seriesFullTracePathMapper };
};
export const generateChart = (chartType, generator = _defaultChartGenerator, config = void 0, mapKeys = (key) => key) => {
  const columnFields = config.columnPivots.flatMap((pivot) => pivot.fields);
  return (results) => {
    const allCharts = results.map(([value, x, values, z, fullPath]) => ({
      type: chartType,
      name: value.split(keySeparator).map((key, idx) => columnFields[idx] ? mapKeys(key, columnFields[idx]) : key).join(humanSeparator),
      labels: x.map((key) => key.join(keySeparator)),
      values,
      data: z,
      originalName: value,
      fullPath
    }));
    return allCharts.map((args, idx) => generator({ ...args, idx, total: allCharts.length, config }));
  };
};
export const removeNulls = () => (results) => results.map(([name, keys, values, z, fullName]) => {
  const nullIndices = Array.from(values).reduce(
    (indices, value, index) => value === null || value === void 0 ? [...indices, index] : indices,
    []
  );
  const newKeys = keys.filter((_, idx) => !nullIndices.includes(idx));
  const newValues = values.filter((_, idx) => !nullIndices.includes(idx));
  return [name, newKeys, newValues, z, fullName];
});
const doNotSuffixTraceForSingleSeries = (keys) => keys.length > 1 ? keys.slice(0, -1).join(keySeparator) : keys[0];
export const chartData = (data, {
  chartType,
  widgetConfig: config,
  generator = _defaultChartGenerator,
  seriesFormatter: customSeriesFormatter = formatSeries,
  leafValueMatcher,
  formatTime,
  mapKeys
}) => {
  const { rowPivots, columnPivots, series } = config;
  return flow([
    transformKeys(rowPivots, columnPivots, formatTime),
    extractSeries(series.length === 1 ? doNotSuffixTraceForSingleSeries : void 0, leafValueMatcher),
    customSeriesFormatter,
    removeNulls(),
    generateChart(chartType, generator, config, mapKeys)
  ])(data);
};
